import React, {useCallback, useEffect, useState} from 'react'

import {MunshiMenu} from "../components/Menu";
import {Card} from "primereact/card";
import {Button} from 'primereact/button'

import {DepartmentReselect} from "../components/Select/DepartmentReselect";
import {GroupByDepartmentSelect} from "../components/Select/GroupByDepartmentSelect";
import {ItemByGroupSelect} from "../components/Select/ItemByGroupSelect";
import {axiosInstance} from "../utilities/AxiosConfig";
import {ToastContainer} from 'react-toastify';
import {TextInput} from "../components/TextInput";
import {useSpinner} from "../context/SpinnerContext";
import {notify} from "../components/toast";
import {FloatLabel} from "primereact/floatlabel";
import {Dropdown} from "primereact/dropdown";
import {ToggleButton} from "primereact/togglebutton";

export function ItemEditor({handle}) {
    const {loading, setLoading} = useSpinner();

    // Grouped selection state
    const [selections, setSelections] = useState({
        department: {id: 0, name: ""},
        group: {id: 0, name: "", kit: false, department: 0},
        item: {id: 0, code: "0000", name: "", itemGroup: 0}
    });

    // Grouped lists state
    const [lists, setLists] = useState({
        departments: [],
        groups: [],
        items: []
    });

    // Control visibility
    const [visibility, setVisibility] = useState({
        showGroup: false,
        showItem: false
    });

    // Helper to refresh data
    const fetchData = useCallback(async (url, targetKey) => {
        setLoading(true);
        try {
            const {data} = await axiosInstance.get(url);
            setLists(prev => ({...prev, [targetKey]: data || []}));
        } catch (error) {
            notify(error.message, "error");
        } finally {
            setLoading(false);
        }
    }, [setLoading]);

    // Initial load: Departments
    useEffect(() => {
        fetchData("department/", "departments");
    }, [fetchData]);

    // Load Groups when Department changes
    useEffect(() => {
        if (selections.department.id) {
            fetchData(`itemGroup/department/${selections.department.id}`, "groups");
        }
    }, [selections.department.id, fetchData]);

    // Load Items when Group changes
    useEffect(() => {
        if (selections.group.id) {
            fetchData(`item/group/${selections.group.id}`, "items");
        }
    }, [selections.group.id, fetchData]);

    const handleDepartmentSelect = (dept) => {
        setSelections(prev => ({
            ...prev,
            department: dept,
            group: {id: 0, name: "", kit: false, department: dept.id},
            item: {id: 0, code: "0000", name: "", itemGroup: 0}
        }));
        setVisibility({showGroup: dept.id !== 0, showItem: false});
    };

    const handleGroupSelect = (group) => {
        setSelections(prev => ({
            ...prev,
            group: group,
            item: {id: 0, code: "0000", name: "", itemGroup: group.id}
        }));
        setVisibility(prev => ({...prev, showItem: group.id !== 0}));
    };

    const handleItemSelect = (item) => {
        setSelections(prev => ({...prev, item}));
    };

    const performAction = async (action, target, data) => {
        setLoading(true);
        try {
            let response;
            if (action === "Save") {
                response = await axiosInstance.post(`${target}/`, data);
                notify(`${target} Saved Successfully`, "success");
            } else if (action === "Delete") {
                response = await axiosInstance.delete(`${target}/${data.id}`);
                if (response.data === 0) throw new Error(`${target} could not be deleted`);
                notify(`${target} Deleted Successfully`, "success");
            }

            // Refresh logic based on target
            if (target === "department") {
                fetchData("department/", "departments");
                handleDepartmentSelect({id: 0, name: ""});
                setVisibility({showGroup: false, showItem: false});
            } else if (target === "itemGroup") {
                fetchData(`itemGroup/department/${selections.department.id}`, "groups");
                handleGroupSelect({id: 0, name: "", department: selections.department.id});
                setVisibility(prev => ({...prev, showItem: false}));
            } else if (target === "item") {
                fetchData(`item/group/${selections.group.id}`, "items");
                handleItemSelect({id: 0, code: "0000", name: "", itemGroup: selections.group.id});
            }
        } catch (error) {
            notify(error.message, "error");
        } finally {
            setLoading(false);
        }
    };

    const EditorActions = ({onSave, onDelete, onReset, isNew}) => (
        <div className="flex gap-2 mt-2">
            <Button icon="pi pi-undo" className="p-button-secondary p-button-sm" onClick={onReset} tooltip="Reset"/>
            <Button icon="pi pi-save" className="p-button-success p-button-sm" onClick={onSave} tooltip="Save"/>
            {!isNew && <Button icon="pi pi-trash" className="p-button-danger p-button-sm" onClick={onDelete}
                               tooltip="Delete"/>}
        </div>
    );
    if (!loading) {
        return (
            <div className="min-h-screen bg-gray-50 pb-6">
                <ToastContainer autoClose={1500} position="top-right" hideProgressBar/>
                <MunshiMenu handle={handle}/>
                <div className="container mx-auto px-3 mt-4">
                    <div className="grid justify-content-center">
                        <div className="col-12 md:col-8 lg:col-6">
                            <Card title={
                                <div className="flex align-items-center gap-2">
                                    <i className="pi pi-box text-primary text-2xl"></i>
                                    <span>Item & Inventory Editor</span>
                                </div>
                            } className="shadow-2 border-round-xl">
                                <div className="flex flex-column gap-5 w-full max-w-30rem mx-auto py-2">
                                    {/* Department */}
                                    <section className="p-3 border-round surface-border border-1 surface-50">
                                        <label className="block mb-2 font-bold text-sm text-600">DEPARTMENT</label>
                                        <DepartmentReselect
                                            department={selections.department}
                                            departmentList={lists.departments}
                                            setDepartment={handleDepartmentSelect}
                                            disabled={false}/>
                                        <div className="mt-4 flex flex-column gap-3">
                                            <FloatLabel>
                                                <TextInput
                                                    placeholder="Department Name"
                                                    value={selections.department.name}
                                                    setValue={(val) => setSelections(prev => ({
                                                        ...prev,
                                                        department: {...prev.department, name: val}
                                                    }))}
                                                    disable={false}/>
                                            </FloatLabel>
                                            <EditorActions
                                                isNew={!selections.department.id}
                                                onReset={() => handleDepartmentSelect({id: 0, name: ""})}
                                                onSave={() => performAction("Save", "department", selections.department)}
                                                onDelete={() => performAction("Delete", "department", selections.department)}
                                            />
                                        </div>
                                    </section>

                                    {/* Group */}
                                    {visibility.showGroup && (
                                        <section
                                            className="p-3 border-round surface-border border-1 surface-50 animation-duration-300 fadein">
                                            <label className="block mb-2 font-bold text-sm text-600">ITEM GROUP</label>
                                            <GroupByDepartmentSelect
                                                group={selections.group}
                                                groupList={lists.groups}
                                                setGroup={handleGroupSelect}
                                                disable={false}/>
                                            <div className="mt-4 flex flex-column gap-3">
                                                <div className="flex flex-column gap-1">
                                                    <label className="text-xs font-semibold text-500">Change Group
                                                        Department</label>
                                                    <DepartmentReselect
                                                        department={{id: selections.group.department}}
                                                        departmentList={lists.departments}
                                                        setDepartment={(dept) => setSelections(prev => ({
                                                            ...prev,
                                                            group: {...prev.group, department: dept.id}
                                                        }))}
                                                        disabled={false}/>
                                                </div>
                                                <div className="flex flex-row md:flex-row gap-3 align-items-end">
                                                    <div className="flex-1">
                                                        <FloatLabel>
                                                            <TextInput
                                                                placeholder="Group Name"
                                                                value={selections.group.name}
                                                                setValue={(val) =>
                                                                    setSelections((prev) => ({
                                                                        ...prev,
                                                                        group: {...prev.group, name: val},
                                                                    }))
                                                                }
                                                                disable={false}
                                                            />
                                                        </FloatLabel>
                                                    </div>

                                                    <div className="md:w-12rem w-full">
                                                        <ToggleButton
                                                            onLabel="Parts"
                                                            offLabel="Complete"
                                                            checked={selections.group.kit}
                                                            onChange={(e) =>
                                                                setSelections((prev) => ({
                                                                    ...prev,
                                                                    group: {...prev.group, kit: e.value},
                                                                }))
                                                            }
                                                            className="w-full h-3rem"
                                                        />
                                                    </div>
                                                </div>
                                                <EditorActions
                                                    isNew={!selections.group.id}
                                                    onReset={() => handleGroupSelect({
                                                        id: 0,
                                                        name: "",
                                                        department: selections.department.id
                                                    })}
                                                    onSave={() => performAction("Save", "itemGroup", selections.group)}
                                                    onDelete={() => performAction("Delete", "itemGroup", selections.group)}
                                                />
                                            </div>
                                        </section>
                                    )}

                                    {/* Item */}
                                    {visibility.showItem && (
                                        <section
                                            className="p-3 border-round surface-border border-1 surface-50 animation-duration-300 fadein">
                                            <label className="block mb-2 font-bold text-sm text-600">ITEM</label>
                                            <ItemByGroupSelect
                                                item={selections.item}
                                                itemList={lists.items}
                                                setItem={handleItemSelect}
                                                disable={false}/>
                                            <div className="mt-4 flex flex-column gap-3">
                                                <FloatLabel>
                                                    <TextInput
                                                        placeholder="Item Name"
                                                        value={selections.item.name}
                                                        setValue={(val) => setSelections(prev => ({
                                                            ...prev,
                                                            item: {...prev.item, name: val}
                                                        }))}
                                                        disable={false}/>
                                                </FloatLabel>

                                                <FloatLabel>
                                                    <Dropdown
                                                        id="itemGroup"
                                                        value={selections.item.itemGroup}
                                                        options={lists.groups}
                                                        onChange={(e) => setSelections(prev => ({
                                                            ...prev,
                                                            item: {...prev.item, itemGroup: e.value}
                                                        }))}
                                                        optionLabel="name"
                                                        optionValue="id"
                                                        placeholder="Change Group"
                                                        className="w-full h-2rem align-items-center"
                                                    />
                                                </FloatLabel>

                                                <EditorActions
                                                    isNew={!selections.item.id}
                                                    onReset={() => handleItemSelect({
                                                        id: 0,
                                                        code: "0000",
                                                        name: "",
                                                        itemGroup: selections.group.id
                                                    })}
                                                    onSave={() => performAction("Save", "item", selections.item)}
                                                    onDelete={() => performAction("Delete", "item", selections.item)}
                                                />
                                            </div>
                                        </section>
                                    )}
                                </div>
                            </Card>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default ItemEditor;
